// Shared config panel for "and_condition" and "or_condition" blocks
// Multiple comparisons combined with AND or OR logic
import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  Alert,
  Paper,
  IconButton,
  Button,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { OPERATORS } from './IfConditionConfig';

const LogicGateConfig = ({ config, onChange, onSummaryChange, mode }) => {
  const isAnd = mode === 'and';
  const comparisons = config.comparisons || [{ variable: '', operator: 'eq', compareValue: '' }];

  const updateComparisons = (newComparisons) => {
    const updated = { ...config, comparisons: newComparisons };
    onChange(updated);
    const count = newComparisons.filter(c => c.variable).length;
    onSummaryChange(
      count > 0
        ? `${count} check${count > 1 ? 's' : ''} (${isAnd ? 'ALL' : 'ANY'})`
        : 'No checks'
    );
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = comparisons.map((c, i) =>
      i === index ? { ...c, [field]: value } : c
    );
    updateComparisons(newEntries);
  };

  const addComparison = () => {
    updateComparisons([...comparisons, { variable: '', operator: 'eq', compareValue: '' }]);
  };

  const removeComparison = (index) => {
    if (comparisons.length <= 1) return;
    updateComparisons(comparisons.filter((_, i) => i !== index));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        {isAnd
          ? <>Branches <strong>TRUE</strong> only if <strong>ALL</strong> comparisons pass. If any fails → FALSE.</>
          : <>Branches <strong>TRUE</strong> if <strong>ANY</strong> comparison passes. All fail → FALSE.</>
        }
      </Typography>

      {comparisons.map((entry, index) => {
        const selectedOp = OPERATORS.find(o => o.value === entry.operator) || OPERATORS[0];
        return (
          <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" fontWeight={600}>Check {index + 1}</Typography>
                {index > 0 && (
                  <Chip
                    label={isAnd ? 'AND' : 'OR'}
                    size="small"
                    color={isAnd ? 'primary' : 'warning'}
                    sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
                  />
                )}
              </Box>
              {comparisons.length > 1 && (
                <IconButton size="small" onClick={() => removeComparison(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <TextField
              size="small"
              label="Variable"
              value={entry.variable || ''}
              onChange={(e) => handleEntryChange(index, 'variable', e.target.value)}
              required
              helperText='Use {{varName}} syntax'
              placeholder="e.g. {{api.statusCode}}"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Operator</InputLabel>
              <Select
                value={entry.operator || 'eq'}
                onChange={(e) => handleEntryChange(index, 'operator', e.target.value)}
                label="Operator"
              >
                {OPERATORS.map(op => (
                  <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedOp.needsValue !== false && (
              <TextField
                size="small"
                label="Compare Value"
                value={entry.compareValue ?? ''}
                onChange={(e) => handleEntryChange(index, 'compareValue', e.target.value)}
                placeholder="e.g. 200"
              />
            )}
          </Paper>
        );
      })}

      <Button size="small" startIcon={<AddIcon />} onClick={addComparison} variant="outlined">
        Add Check
      </Button>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          {isAnd ? (
            <>
              <strong>AND logic:</strong> Every check must pass for the block to branch TRUE.<br />
              Use this when all conditions must be met simultaneously.<br />
              <em>Example: statusCode == 200 AND matchCount {'>'} 0</em>
            </>
          ) : (
            <>
              <strong>OR logic:</strong> At least one check must pass for the block to branch TRUE.<br />
              Use this when any single condition is sufficient.<br />
              <em>Example: priority == high OR priority == critical</em>
            </>
          )}
        </Typography>
      </Alert>
    </Box>
  );
};

// Convenience wrappers
const AndConditionConfig = (props) => <LogicGateConfig {...props} mode="and" />;
const OrConditionConfig = (props) => <LogicGateConfig {...props} mode="or" />;

export { AndConditionConfig, OrConditionConfig };
export default LogicGateConfig;
